import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ImageUpload from '../ImageUpload';
import MessagingSystem from '../messaging/MessagingSystem';
import RewardsTab, { PointsBadge } from '../rewards/RewardsTab';
import VerificationBanner from '../VerificationBanner';
import VerificationFlow from '../VerificationFlow';
import { ALL_AMENITIES } from '../../data/amenities';

// Full default amenity list for the form chips — single source of truth
const DEFAULT_AMENITY_LIST = [
  'WiFi', 'Internet', '1 Bathroom', '2 Bathrooms', '1 Toilet', '2 Toilets',
  'Common Room', 'AC', 'Heating', 'Washing Machine', 'Dryer',
  'Gym', 'Pool', 'Parking (Free)', 'Parking (Street)', 'Parking (Paid)',
  'Balcony', 'Courtyard', 'Garden', 'Security', 'Fireplace',
  'Study Room', 'Bike Storage', 'Couples OK', 'Pets Allowed',
];
import {
  Building2, MessageSquare, User, Settings, Plus,
  Bell, LogOut, MapPin, Calendar, Trash2, Eye, Edit,
  CheckCircle, Shield, Upload, Trophy, Copy, Check, ExternalLink, Menu
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// ── Private listing shareable link component ──────────────────────────────────
function PrivateListingLink({ token }) {
  const [copied, setCopied] = React.useState(false);
  const url = `https://rentalx.io/listing/private/${token}`;
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 mb-1">
      <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wide mb-1.5">🔒 Private Shareable Link</p>
      <div className="flex items-center gap-1.5">
        <code className="flex-1 text-[10px] text-amber-800 bg-white border border-amber-200 rounded-lg px-2 py-1 truncate font-mono">
          {url}
        </code>
        <button onClick={copy}
          className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-white transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}>
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
      </div>
    </div>
  );
}

const AU_CITIES_BY_STATE = {
  'NSW': ['Sydney','Newcastle','Wollongong','Central Coast','Maitland','Bathurst','Albury','Wagga Wagga','Port Macquarie','Dubbo','Orange','Tamworth','Armidale','Lismore','Coffs Harbour','Broken Hill'],
  'VIC': ['Melbourne','Geelong','Ballarat','Bendigo','Shepparton','Wodonga','Warrnambool','Mildura','Latrobe Valley','Horsham'],
  'QLD': ['Brisbane','Gold Coast','Sunshine Coast','Townsville','Cairns','Toowoomba','Mackay','Rockhampton','Ipswich','Bundaberg','Hervey Bay','Gladstone','Mount Isa'],
  'WA':  ['Perth','Mandurah','Bunbury','Geraldton','Kalgoorlie','Albany','Broome','Port Hedland','Karratha'],
  'SA':  ['Adelaide','Mount Gambier','Whyalla','Murray Bridge','Victor Harbor','Port Lincoln','Port Augusta'],
  'ACT': ['Canberra'],
  'TAS': ['Hobart','Launceston','Devonport','Burnie'],
  'NT':  ['Darwin','Palmerston','Alice Springs','Katherine'],
};
const ALL_AU_CITIES = Object.values(AU_CITIES_BY_STATE).flat();
const AU_STATES = ['NSW','VIC','QLD','WA','SA','ACT','TAS','NT'];

const EMPTY_FORM = {
  name: '', city: '', description: '',
  street_address: '', apt_no: '', suburb: '', postcode: '', state: '',
  price_per_week: '', room_type: 'Private Room',
  bedrooms: 1, bathrooms: 1,
  university_nearby: '', available_from: '', available_until: '',
  amenities: [],          // will be set to ALL_AMENITIES on mount — see useEffect below
  customAmenities: [],
  images: [],
  furnishing: 'Furnished', lease_term: 'Flexible',
  parking: 'Not Available', pets: 'Not Allowed',
  bills_included: 'Some Bills Included',
  room_features: [], kitchen_features: [],
  customAmenities: [], newAmenity: '',
  visibility: 'public',
};

const LandlordDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  const [activeTab, setActiveTab] = useState('properties');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const switchTab = (id) => { setActiveTab(id); setSidebarOpen(false); };
  const [editingId, setEditingId] = useState(null);  // moved up for useEffect dependency
  const [editingStatus, setEditingStatus] = useState(null); // tracks the status of the listing being edited
  const [validationErrors, setValidationErrors] = useState({});
  const [showVerifyFlow, setShowVerifyFlow] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Load landlord's properties on mount
  useEffect(() => {
    if (!token) return;
    setLoadingProps(true);
    fetch(`${API}/api/properties/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setProperties(Array.isArray(data) ? data : []))
      .catch(() => setProperties([]))
      .finally(() => setLoadingProps(false));
  }, [token, API]);

  const [requirements, setRequirements] = useState([]);
  const [reqLoading, setReqLoading] = useState(true);
  const [contactReq, setContactReq] = useState(null);
  const [viewReq, setViewReq] = useState(null);
  const [contactMsg, setContactMsg] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [msgSent, setMsgSent] = useState(false);

  const sendContactMessage = async () => {
    if (!contactMsg.trim() || !contactReq) return;
    setSendingMsg(true);
    try {
      const r = await fetch(`${API}/api/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ recipient_id: contactReq.user_id, content: contactMsg }),
      });
      if (r.ok) {
        setMsgSent(true);
        setContactMsg('');
        setTimeout(() => { setContactReq(null); setMsgSent(false); switchTab('messages'); }, 1500);
      }
    } catch { /* silent */ }
    setSendingMsg(false);
  };

  useEffect(() => {
    fetch(`${API}/api/requirements/public`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setRequirements(Array.isArray(data) ? data : []))
      .catch(() => setRequirements([]))
      .finally(() => setReqLoading(false));
  }, []);

  // Pre-select ALL amenities when opening a NEW listing form (not when editing)
  useEffect(() => {
    if (activeTab === 'add-property' && !editingId) {
      setForm(prev => ({ ...prev, amenities: [...DEFAULT_AMENITY_LIST] }));
    }
  }, [activeTab, editingId]);

  const handleInput = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  // ── Field validation ─────────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {};
    if (!form.name?.trim() || form.name.trim().length < 3) errors.name = 'Listing title is required (min 3 characters)';
    if (!form.city?.trim()) errors.city = 'City is required';
    if (!form.price_per_week || parseFloat(form.price_per_week) <= 0) errors.price_per_week = 'Valid weekly price is required';
    if (!form.room_type) errors.room_type = 'Room type is required';
    if (!form.description?.trim()) errors.description = 'Description is required';
    if (!form.bedrooms || parseInt(form.bedrooms) < 1) errors.bedrooms = 'Bedrooms required';
    if (!form.bathrooms || parseInt(form.bathrooms) < 1) errors.bathrooms = 'Bathrooms required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const addressParts = [form.street_address, form.apt_no, form.suburb, form.state, form.postcode].filter(Boolean);
      const fullAddress = addressParts.join(', ');
      const targetId = editingId || draftId;
      const method = targetId ? 'PUT' : 'POST';
      const url = targetId ? `${API}/api/properties/${targetId}` : `${API}/api/properties`;
      // When editing a non-draft listing, preserve existing status; new listings go to pending_review
      const newStatus = editingId && editingStatus !== 'draft' ? editingStatus : 'pending_review';
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify({
          ...form,
          address: fullAddress,
          price_per_week: parseFloat(form.price_per_week),
          bedrooms: parseInt(form.bedrooms),
          bathrooms: parseInt(form.bathrooms),
          status: newStatus,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setSubmitError(typeof data.detail === 'string' ? data.detail : Array.isArray(data.detail) ? data.detail.map(e => e.msg).join(', ') : 'Failed'); return; }
      if (targetId) {
        setProperties(prev => prev.map(p => p.id === targetId ? data : p));
      } else {
        setProperties(prev => [data, ...prev]);
      }
      setForm(EMPTY_FORM);
      setForm(prev => ({ ...prev, amenities: ALL_AMENITIES.map(a => a.id) }));
      setEditingId(null);
      setEditingStatus(null);
      setDraftId(null);
      setDraftSaved(false);
      setValidationErrors({});
      setSubmitSuccess(true);
      setTimeout(() => { setSubmitSuccess(false); setActiveTab('properties'); }, 2000);
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit for Review: only available when editing a draft
  const handleSubmitForReview = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const addressParts = [form.street_address, form.apt_no, form.suburb, form.state, form.postcode].filter(Boolean);
      const res = await fetch(`${API}/api/properties/${editingId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ...form,
          address: addressParts.join(', ') || form.address || '',
          price_per_week: parseFloat(form.price_per_week),
          bedrooms: parseInt(form.bedrooms),
          bathrooms: parseInt(form.bathrooms),
          status: 'pending_review',
        }),
      });
      const data = await res.json();
      if (!res.ok) { setSubmitError(typeof data.detail === 'string' ? data.detail : 'Submission failed'); return; }
      setProperties(prev => prev.map(p => p.id === editingId ? data : p));
      setForm(EMPTY_FORM);
      setForm(prev => ({ ...prev, amenities: ALL_AMENITIES.map(a => a.id) }));
      setEditingId(null);
      setEditingStatus(null);
      setDraftId(null);
      setDraftSaved(false);
      setValidationErrors({});
      setSubmitSuccess(true);
      setTimeout(() => { setSubmitSuccess(false); setActiveTab('properties'); }, 2000);
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const [draftId, setDraftId] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);

  const handleSaveDraft = async () => {
    if (!form.city) return;
    const isUpdate = editingId || draftId;
    const method = isUpdate ? 'PUT' : 'POST';
    const url = isUpdate ? `${API}/api/properties/${editingId || draftId}` : `${API}/api/properties`;
    const addressParts = [form.street_address, form.apt_no, form.suburb, form.state, form.postcode].filter(Boolean);
    try {
      const res = await fetch(url, {
        method, headers,
        body: JSON.stringify({
          ...form,
          address: addressParts.join(', ') || form.address || '',
          price_per_week: parseFloat(form.price_per_week) || 0,
          bedrooms: parseInt(form.bedrooms),
          bathrooms: parseInt(form.bathrooms),
          status: 'draft',
        }),
      });
      const data = await res.json();
      if (res.ok) {
        if (!isUpdate) {
          setDraftId(data.id);
          setProperties(prev => [data, ...prev]);
        } else {
          setProperties(prev => prev.map(p => p.id === (editingId || draftId) ? data : p));
        }
        setDraftSaved(true);
      }
    } catch { /* silent */ }
  };

  const handleEdit = (prop) => {
    setDraftId(null);
    setDraftSaved(false);
    setValidationErrors({});
    setEditingStatus(prop.status || null); // remember current status
    // Identify custom amenities: any that aren't in the global ALL_AMENITIES list
    const defaultIds = new Set(ALL_AMENITIES.map(a => a.id));
    const savedAmenities = prop.amenities || [];
    const custom = savedAmenities.filter(a => !defaultIds.has(a));
    setForm({
      ...EMPTY_FORM,
      name: prop.name || '',
      city: prop.city || '',
      description: prop.description || '',
      street_address: prop.street_address || prop.address?.split(',')[0]?.trim() || '',
      apt_no: prop.apt_no || '',
      suburb: prop.suburb || prop.address?.split(',')[1]?.trim() || '',
      postcode: prop.postcode || '',
      state: prop.state || '',
      price_per_week: String(prop.price_per_week || ''),
      room_type: prop.room_type || 'Private Room',
      bedrooms: prop.bedrooms || 1,
      bathrooms: prop.bathrooms || 1,
      university_nearby: prop.university_nearby || '',
      available_from: prop.available_from || '',
      available_until: prop.available_until || '',
      amenities: savedAmenities,      // restore exact saved state
      customAmenities: custom,        // restore custom additions
      images: prop.images || [],
      furnishing: prop.furnishing || 'Furnished',
      lease_term: prop.lease_term || 'Flexible',
      parking: prop.parking || 'Not Available',
      pets: prop.pets || 'Not Allowed',
      bills_included: prop.bills_included || 'Some Bills Included',
      room_features: prop.room_features || [],
      kitchen_features: prop.kitchen_features || [],
      visibility: prop.visibility || 'public',
    });
    setEditingId(prop.id);
    setActiveTab('add-property');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this property?')) return;
    const res = await fetch(`${API}/api/properties/${id}`, { method: 'DELETE', headers });
    if (res.ok || res.status === 204) {
      setProperties(prev => prev.filter(p => p.id !== id));
    }
  };

  // Landlord KYC state
  const handleLogout = () => { logout(); navigate('/'); };

  // KYC state
  const [kycStatus, setKycStatus] = useState('unverified');
  const [kycFiles, setKycFiles] = useState({ govId: null, ownership: null });
  const [abn, setAbn] = useState('');
  const [licenceNo, setLicenceNo] = useState('');

  const handleKycSubmit = () => {
    if (kycFiles.govId && kycFiles.ownership) setKycStatus('pending');
  };

  const sidebarGroups = [
    {
      label: 'Listings',
      items: [
        { id: 'properties',    label: 'My Properties', icon: Building2 },
        { id: 'add-property',  label: 'Add Property',  icon: Plus },
      ],
    },
    {
      label: 'Renters',
      items: [
        { id: 'requirements', label: 'Student Leads', icon: MessageSquare },
        { id: 'messages',     label: 'Messages',      icon: MessageSquare, badge: 2 },
      ],
    },
    {
      label: 'Account',
      items: [
        { id: 'verify',   label: 'Verify ID', icon: Shield },
        { id: 'rewards',  label: 'Rewards',   icon: Trophy },
        { id: 'profile',  label: 'Profile',   icon: User },
        { id: 'settings', label: 'Settings',  icon: Settings },
      ],
    },
  ];

  const TAB_LABELS = {
    properties: 'My Properties', 'add-property': 'Add Property',
    requirements: 'Student Leads', messages: 'Messages',
    verify: 'Verify ID', rewards: 'Rewards', profile: 'Profile', settings: 'Settings',
  };

  const userInitials = user?.full_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'LL';

  const renderPropertiesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">My Properties</h2>
        <Button onClick={() => setActiveTab('add-property')} className="bg-[#094f6f] text-white hover:bg-[#094f6f]/90">
          <Plus className="w-4 h-4 mr-2" />
          Add Property
        </Button>
      </div>

      {loadingProps ? (
        <div className="text-center py-12 text-gray-500">Loading properties...</div>
      ) : properties.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Properties Yet</h3>
            <p className="text-gray-500 mb-6">Add your first property to start connecting with students.</p>
            <Button onClick={() => setActiveTab('add-property')} className="bg-[#094f6f] text-white hover:bg-[#094f6f]/90">
              Add Your First Property
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((prop) => (
            <Card key={prop.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="h-40 bg-gradient-to-br from-sky-100 to-blue-50 flex items-center justify-center relative overflow-hidden">
                {prop.images?.[0] ? (
                  <img src={prop.images[0]} alt={prop.name} className="w-full h-full object-cover" />
                ) : (
                  <Building2 className="w-12 h-12 text-[#094f6f]/40" />
                )}
              </div>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-800 truncate flex-1 mr-2">{prop.name}</h3>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {prop.visibility === 'private' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">🔒 Private</span>
                    )}
                    <span className={`px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                      prop.status === 'active' ? 'bg-green-100 text-green-700' :
                      prop.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                      prop.status === 'rejected' ? 'bg-red-100 text-red-600' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {prop.status === 'active' ? '✓ Live' :
                       prop.status === 'pending_review' ? '⏳ Pending Review' :
                       prop.status === 'rejected' ? '✕ Rejected' : prop.status}
                    </span>
                  </div>
                </div>
                <p className="text-gray-500 text-sm flex items-center mb-1">
                  <MapPin className="w-3 h-3 mr-1" /> {prop.city}
                </p>
                <p className="text-[#094f6f] font-bold text-lg mb-3">
                  A${prop.price_per_week}<span className="text-sm font-normal text-gray-500">/wk</span>
                </p>

                {/* Private link */}
                {prop.visibility === 'private' && prop.private_token && (
                  <PrivateListingLink token={prop.private_token} />
                )}

                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="flex-1 text-xs"
                    onClick={() => navigate(`/property/${prop.id}`)}>
                    <Eye className="w-3 h-3 mr-1" /> View
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handleEdit(prop)}>
                    <Edit className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50 text-xs" onClick={() => handleDelete(prop.id)} data-testid="prop-delete-btn">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderAddPropertyTab = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-2">
        <Button onClick={() => setActiveTab('properties')} variant="outline" size="sm">
          ← Back
        </Button>
        <h2 className="text-2xl font-bold text-gray-800">{editingId ? 'Edit Property' : 'Add New Property'}</h2>
      </div>

      {submitSuccess && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <CheckCircle className="w-5 h-5" />
          Property listed successfully! Redirecting...
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-[#094f6f]">Property Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name + City */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Name *</label>
                <Input value={form.name} onChange={e => { handleInput('name', e.target.value); if (validationErrors.name) setValidationErrors(p => ({ ...p, name: '' })); }}
                  placeholder="e.g., Modern Studio near UniMelb" required
                  className={`${validationErrors.name ? 'border-red-400 focus:border-red-500' : 'border-sky-200 focus:border-[#094f6f]'}`} data-testid="prop-name" />
                {validationErrors.name && <p className="text-xs text-red-500 mt-1">{validationErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <Select value={form.city} onValueChange={v => handleInput('city', v)}>
                  <SelectTrigger className="border-sky-200 focus:border-[#094f6f]">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 overflow-y-auto">
                    {Object.entries(AU_CITIES_BY_STATE).map(([state, cities]) => (
                      <React.Fragment key={state}>
                        <div className="px-2 py-1 text-xs font-bold text-gray-400 uppercase tracking-wide bg-gray-50">{state}</div>
                        {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Structured Address */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <Input value={form.street_address} onChange={e => handleInput('street_address', e.target.value)}
                  placeholder="e.g., 123 Collins Street"
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apartment / Unit / House No. <span className="text-gray-400">(optional)</span></label>
                <Input value={form.apt_no} onChange={e => handleInput('apt_no', e.target.value)}
                  placeholder="e.g., Unit 4, Apt 12B"
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Suburb</label>
                <Input value={form.suburb} onChange={e => handleInput('suburb', e.target.value)}
                  placeholder="e.g., Carlton"
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                <Input value={form.postcode} onChange={e => handleInput('postcode', e.target.value.replace(/\D/g,'').slice(0,4))}
                  placeholder="e.g., 3053" maxLength={4}
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <Select value={form.state} onValueChange={v => handleInput('state', v)}>
                  <SelectTrigger className="border-sky-200"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    {AU_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Price + Room type + Bedrooms + Bathrooms */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price/Week (AUD) *</label>
                <Input type="number" value={form.price_per_week} onChange={e => handleInput('price_per_week', e.target.value)}
                  placeholder="450" required min="1"
                  className="border-sky-200 focus:border-[#094f6f]" data-testid="prop-price" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                <Select value={form.room_type} onValueChange={v => handleInput('room_type', v)}>
                  <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Private Room','Shared Room','Studio','1-Bedroom','2-Bedroom','Entire House'].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                <Select value={String(form.bedrooms)} onValueChange={v => handleInput('bedrooms', parseInt(v))}>
                  <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                <Select value={String(form.bathrooms)} onValueChange={v => handleInput('bathrooms', parseInt(v))}>
                  <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* University nearby + Available from + Available until */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nearby University</label>
                <Input value={form.university_nearby} onChange={e => handleInput('university_nearby', e.target.value)}
                  placeholder="e.g., University of Melbourne"
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Available From</label>
                <Input type="date" value={form.available_from} onChange={e => handleInput('available_from', e.target.value)}
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Available Until <span className="text-gray-400">(leave blank if ongoing)</span>
                </label>
                <Input type="date" value={form.available_until || ''} onChange={e => handleInput('available_until', e.target.value)}
                  min={form.available_from || undefined}
                  className="border-sky-200 focus:border-[#094f6f]" />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea value={form.description} onChange={e => { handleInput('description', e.target.value); if (validationErrors.description) setValidationErrors(p => ({ ...p, description: '' })); }}
                placeholder="Describe your property, nearby facilities, transportation links..."
                rows={4} required
                className="w-full px-3 py-2 border border-sky-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#094f6f] text-sm"
                data-testid="prop-description" />
            </div>

            {/* Extra fields row */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'furnishing', label: 'Furnishing', opts: ['Furnished', 'Unfurnished', 'Partially Furnished'] },
                { key: 'lease_term', label: 'Minimum Lease', opts: ['Flexible', '1 Month', '3 Months', '6 Months', '12 Months'] },
                { key: 'parking', label: 'Parking', opts: ['Not Available', 'Available (Free)', 'Available (Extra Cost)'] },
                { key: 'pets', label: 'Pet Policy', opts: ['Not Allowed', 'Allowed', 'Negotiable'] },
                { key: 'bills_included', label: 'Bills', opts: ['Not Included', 'All Bills Included', 'Some Bills Included', 'Water & Internet Only'] },
              ].map(({ key, label, opts }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <Select value={form[key]} onValueChange={v => handleInput(key, v)}>
                    <SelectTrigger className="border-sky-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {opts.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Amenities — all selected by default, click to deselect */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
              <p className="text-xs text-gray-400 mb-2">All amenities are included by default. Click any to remove ones that don't apply to your property.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {DEFAULT_AMENITY_LIST.concat(form.customAmenities).map(a => (
                  <button key={a} type="button"
                    onClick={() => handleInput('amenities', form.amenities.includes(a) ? form.amenities.filter(x => x !== a) : [...form.amenities, a])}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.amenities.includes(a) ? 'text-white border-transparent' : 'border-sky-200 text-gray-600 hover:border-[#094f6f]'}`}
                    style={form.amenities.includes(a) ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
                    {a}
                  </button>
                ))}
              </div>
              {/* Custom amenity creator */}
              <div className="flex gap-2 items-center">
                <Input
                  value={form.newAmenity || ''}
                  onChange={e => handleInput('newAmenity', e.target.value)}
                  placeholder="Type a new amenity..."
                  className="border-sky-200 focus:border-[#094f6f] h-8 text-xs flex-1"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && form.newAmenity?.trim()) {
                      e.preventDefault();
                      const a = form.newAmenity.trim();
                      handleInput('customAmenities', [...(form.customAmenities || []), a]);
                      handleInput('amenities', [...form.amenities, a]);
                      handleInput('newAmenity', '');
                    }
                  }}
                />
                <Button type="button" size="sm"
                  className="h-8 text-xs brand-gradient-bg text-white px-3"
                  onClick={() => {
                    const a = (form.newAmenity || '').trim();
                    if (!a) return;
                    handleInput('customAmenities', [...(form.customAmenities || []), a]);
                    handleInput('amenities', [...form.amenities, a]);
                    handleInput('newAmenity', '');
                  }}>
                  + Create Amenity
                </Button>
              </div>
            </div>

            {/* Room Features */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Room Features</label>
              <div className="flex flex-wrap gap-2">
                {['1 Single Bed','2 Single Beds','1 Double Bed','1 Queen Bed','1 King Bed',
                  'Mattress Included','Wardrobe','Study Desk','Study Chair','Desk Lamp',
                  'Bedside Table','Built-in Shelving'].map(f => (
                  <button key={f} type="button"
                    onClick={() => handleInput('room_features', (form.room_features||[]).includes(f) ? (form.room_features||[]).filter(x=>x!==f) : [...(form.room_features||[]),f])}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${(form.room_features||[]).includes(f) ? 'text-white border-transparent' : 'border-sky-200 text-gray-600 hover:border-[#094f6f]'}`}
                    style={(form.room_features||[]).includes(f) ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Kitchen Features */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Kitchen Features</label>
              <div className="flex flex-wrap gap-2">
                {['Microwave','Toaster','Hot Water Jug / Kettle','Refrigerator','2 Refrigerators',
                  'Oven & Stovetop','Dishwasher','Coffee Machine','Blender','Rice Cooker',
                  'Shared Kitchen','Private Kitchen'].map(f => (
                  <button key={f} type="button"
                    onClick={() => handleInput('kitchen_features', (form.kitchen_features||[]).includes(f) ? (form.kitchen_features||[]).filter(x=>x!==f) : [...(form.kitchen_features||[]),f])}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${(form.kitchen_features||[]).includes(f) ? 'text-white border-transparent' : 'border-sky-200 text-gray-600 hover:border-[#094f6f]'}`}
                    style={(form.kitchen_features||[]).includes(f) ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Property Photos <span className="text-gray-400">(up to 5, first image is the cover)</span>
              </label>
              <ImageUpload
                images={form.images}
                onChange={urls => handleInput('images', urls)}
                folder="rentalx/properties"
              />
            </div>

            {/* Visibility toggle — positioned directly above the form action buttons */}
            <div className="border-2 border-[#094f6f]/20 bg-[#094f6f]/3 rounded-2xl p-4">
              <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                <span>Listing Visibility</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { val: 'public',  icon: '🌐', label: 'Public',  sub: 'Visible in all search results' },
                  { val: 'private', icon: '🔒', label: 'Private', sub: 'Shareable link only, not in search' },
                ].map(opt => (
                  <button key={opt.val} type="button" onClick={() => setForm(f => ({ ...f, visibility: opt.val }))}
                    data-testid={`visibility-${opt.val}-btn`}
                    className={`flex items-start gap-2.5 p-3 rounded-xl border-2 text-left transition-all ${
                      form.visibility === opt.val ? 'border-[#094f6f] bg-white shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}>
                    <span className="text-lg mt-0.5">{opt.icon}</span>
                    <div>
                      <p className={`text-sm font-bold ${form.visibility === opt.val ? 'text-[#094f6f]' : 'text-gray-700'}`}>{opt.label}</p>
                      <p className="text-xs text-gray-400">{opt.sub}</p>
                    </div>
                  </button>
                ))}
              </div>
              {form.visibility === 'private' && (
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-3">
                  🔒 A unique private link will be generated after submission. Only people with the link can view this listing.
                </p>
              )}
            </div>

            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">{submitError}</div>
            )}

            {/* Inline validation error summary */}
            {Object.keys(validationErrors).length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                <p className="font-semibold mb-1">Please complete all required fields before submitting:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {Object.values(validationErrors).map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}

            {/* ── Form Footer Buttons ─────────────────────────────────────────── */}
            <div className="flex justify-between pt-2 flex-wrap gap-2">
              <Button type="button" variant="outline"
                onClick={() => { setActiveTab('properties'); setDraftId(null); setDraftSaved(false); setEditingId(null); setEditingStatus(null); setValidationErrors({}); }}>
                Cancel
              </Button>
              <div className="flex gap-2">
                {/* CASE A — editing a DRAFT: show Save as Draft (secondary) + Submit for Review (primary) */}
                {editingId && editingStatus === 'draft' ? (
                  <>
                    <Button type="button" variant="outline"
                      className="border-gray-300 text-gray-600 hover:bg-gray-50 px-6"
                      onClick={handleSaveDraft}
                      disabled={submitting}
                      data-testid="draft-save-btn">
                      {draftSaved ? '✓ Draft Saved' : 'Save as Draft'}
                    </Button>
                    <Button type="button"
                      className="bg-[#094f6f] text-white hover:opacity-90 px-8 font-bold"
                      onClick={handleSubmitForReview}
                      disabled={submitting}
                      data-testid="submit-for-review-btn">
                      {submitting ? 'Submitting…' : 'Submit for Review'}
                    </Button>
                  </>
                ) : editingId ? (
                  /* CASE B — editing a non-draft (pending_review, active, archived): Save Changes only */
                  <Button type="submit"
                    className="bg-[#094f6f] text-white hover:opacity-90 px-8"
                    disabled={submitting}
                    data-testid="save-changes-btn">
                    {submitting ? 'Saving…' : 'Save Changes'}
                  </Button>
                ) : (
                  /* CASE C — new listing: Save as Draft (secondary) + Submit for Review (primary) */
                  <>
                    <Button type="button" variant="outline"
                      disabled={!form.city || draftSaved}
                      className={draftSaved ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed px-6' : 'border-gray-300 text-gray-600 hover:bg-gray-50 px-6'}
                      onClick={handleSaveDraft}
                      data-testid="draft-save-btn">
                      {draftSaved ? '✓ Draft Saved' : 'Save as Draft'}
                    </Button>
                    <Button type="submit"
                      disabled={submitting || !form.city}
                      className="bg-[#094f6f] text-white hover:opacity-90 px-8 font-bold"
                      data-testid="submit-for-review-btn">
                      {submitting ? 'Submitting…' : 'Submit for Review'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  const renderRequirementsTab = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800">Student Leads</h2>
      <p className="text-gray-500 text-sm">Students actively looking for accommodation</p>
      {reqLoading ? (
        <div className="text-center py-8 text-gray-400">Loading student leads...</div>
      ) : requirements.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-sky-100">
          <MessageSquare className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500">No student requirements posted yet.</p>
          <p className="text-xs text-gray-400 mt-1">Students post their accommodation needs here.</p>
        </div>
      ) : requirements.map((req, i) => (
        <Card key={req.id || i}>
          <CardContent className="p-5">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-gray-800">{req.title}</h3>
                <p className="text-sm text-gray-500">{req.student_name} · {req.roomType || 'Any room type'}</p>
              </div>
              <span className="text-[#094f6f] font-bold">A${req.budget}/wk</span>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-4">
              <span className="flex items-center"><MapPin className="w-3 h-3 mr-1" />{req.location}</span>
              {req.moveInDate && <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />Move-in {req.moveInDate}</span>}
            </div>
            {req.description && <p className="text-sm text-gray-500 mb-3 line-clamp-2">{req.description}</p>}
            <div className="flex gap-2">
              <Button size="sm" className="text-white" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                onClick={() => { setContactReq(req); setContactMsg(''); setMsgSent(false); }}>
                Contact Student
              </Button>
              <Button size="sm" variant="outline" onClick={() => setViewReq(req)}>View Profile</Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderVerifyTab = () => {
    const fileBox = (id, title, subtitle, file, setFile) => (
      <div className="border-2 border-dashed border-sky-200 rounded-xl p-5 text-center hover:border-[#094f6f] transition-colors">
        <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm font-medium text-gray-700 mb-0.5">{title}</p>
        <p className="text-xs text-gray-400 mb-3">{subtitle}</p>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFile(e.target.files[0])} className="hidden" id={id} />
        <label htmlFor={id} className="cursor-pointer bg-[#094f6f] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#094f6f]/90 transition-colors">
          Choose File
        </label>
        {file && <p className="text-xs text-green-600 mt-2">✓ {file.name}</p>}
      </div>
    );

    return (
      <div className="space-y-5 max-w-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Shield className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Property Manager / Landlord Verification</h2>
            <p className="text-sm text-gray-500">Verify your identity and property ownership to earn the Verified Property Manager / Landlord badge</p>
          </div>
        </div>

        {/* Benefits of getting verified */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: '🏅', label: 'Verified Badge', sub: 'Shown on every listing' },
            { icon: '📈', label: '40% More Enquiries', sub: 'vs unverified landlords' },
            { icon: '🔒', label: 'Student Trust', sub: 'Students book with confidence' },
          ].map((b, i) => (
            <div key={i} className="text-center p-3 bg-sky-50 rounded-xl border border-sky-100">
              <div className="text-2xl mb-1">{b.icon}</div>
              <p className="text-xs font-semibold text-gray-800">{b.label}</p>
              <p className="text-[10px] text-gray-500">{b.sub}</p>
            </div>
          ))}
        </div>

        {/* Status banner */}
        <div className={`p-4 rounded-xl border flex items-center gap-3 ${
          kycStatus === 'verified' ? 'bg-green-50 border-green-200' :
          kycStatus === 'pending' ? 'bg-yellow-50 border-yellow-200' :
          'bg-gray-50 border-gray-200'
        }`}>
          {kycStatus === 'verified' && <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />}
          {kycStatus === 'pending' && <div className="w-6 h-6 rounded-full border-2 border-yellow-500 border-t-transparent animate-spin flex-shrink-0" />}
          {kycStatus === 'unverified' && <Shield className="w-6 h-6 text-gray-400 flex-shrink-0" />}
          <div>
            <p className="font-semibold text-sm">
              {kycStatus === 'verified' ? '✅ Verified Property Manager / Landlord' :
               kycStatus === 'pending' ? '⏳ Verification in Progress (1–2 business days)' : '⚠️ Not Yet Verified'}
            </p>
            <p className="text-xs text-gray-500">
              {kycStatus === 'verified' ? 'Your identity and property ownership are confirmed. Verified badge is live on your listings.' :
               kycStatus === 'pending' ? "We're reviewing your documents. We'll notify you by email once approved." :
               'Upload the required documents below to get your Verified Property Manager / Landlord badge.'}
            </p>
          </div>
        </div>

        {kycStatus === 'unverified' && (
          <div className="space-y-5">
            {/* Required section */}
            <div className="bg-white rounded-2xl border border-sky-100 p-5">
              <p className="text-sm font-bold text-gray-800 mb-1">Required Documents</p>
              <p className="text-xs text-gray-500 mb-4">Both documents must be uploaded to complete verification.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fileBox(
                  'landlord-gov-id',
                  'Government-issued ID',
                  'Passport or Australian Driver\'s Licence',
                  kycFiles.govId,
                  f => setKycFiles(p => ({...p, govId: f}))
                )}
                {fileBox(
                  'landlord-ownership',
                  'Proof of Property Ownership',
                  'Council rates notice, title deed, or signed lease',
                  kycFiles.ownership,
                  f => setKycFiles(p => ({...p, ownership: f}))
                )}
              </div>
            </div>

            {/* Optional section */}
            <div className="bg-white rounded-2xl border border-sky-100 p-5">
              <p className="text-sm font-bold text-gray-800 mb-1">Optional <span className="text-gray-400 font-normal">(adds further trust to your profile)</span></p>
              <div className="space-y-3 mt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">ABN <span className="text-gray-400">(for corporate / company landlords)</span></label>
                  <input type="text" value={abn} onChange={e => setAbn(e.target.value)}
                    placeholder="e.g. 51 824 753 556,  auto-verified against ABR"
                    className="w-full px-3 py-2.5 border border-sky-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Real Estate Agent Licence Number <span className="text-gray-400">(licensed agents only)</span></label>
                  <input type="text" value={licenceNo} onChange={e => setLicenceNo(e.target.value)}
                    placeholder="e.g. NSW-REA-123456, verified against state register"
                    className="w-full px-3 py-2.5 border border-sky-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30" />
                </div>
              </div>
            </div>

            <button
              onClick={handleKycSubmit}
              disabled={!kycFiles.govId || !kycFiles.ownership}
              className="w-full bg-green-600 text-white py-3.5 rounded-xl font-semibold disabled:opacity-40 hover:bg-green-700 transition-colors"
              data-testid="landlord-kyc-submit">
              Submit for Verification
            </button>
            <p className="text-xs text-gray-400 text-center">Documents are encrypted and used only for identity and ownership verification. Complies with Australia&apos;s Privacy Act 1988.</p>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'properties': return renderPropertiesTab();
      case 'add-property': return renderAddPropertyTab();
      case 'requirements': return renderRequirementsTab();
      case 'messages': return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-800">Messages</h2>
          <p className="text-sm text-gray-500">Chat with students about your properties</p>
          <MessagingSystem userType="landlord" />
        </div>
      );
      case 'verify': return renderVerifyTab();
      case 'rewards': return <RewardsTab token={token} userRole="landlord" />;
      default: return <div className="text-center py-8 text-gray-500">Coming Soon</div>;
    }
  };

  return (
    <>
    <div className="min-h-screen bg-[#f4f6f9] flex">

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="sidebar-backdrop" />
      )}

      {/* ── Sidebar ── */}
      <aside className={`w-60 bg-white border-r border-gray-200 min-h-screen flex flex-col flex-shrink-0 fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center gap-0 px-5 py-5 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-0">
            <span className="text-xl font-black tracking-tight" style={{ color: '#094f6f' }}>Rental</span>
            <img src="/rentalx-x-new.svg" alt="X"
              style={{ height: '1.3rem', width: 'auto', marginLeft: '0.04em', marginBottom: '0.02em', verticalAlign: 'middle' }} />
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {sidebarGroups.map(group => (
            <div key={group.label} className="mb-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 px-3 mb-1.5">{group.label}</p>
              {group.items.map(item => (
                <button key={item.id} onClick={() => switchTab(item.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all mb-0.5 ${
                    activeTab === item.id ? 'text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  style={activeTab === item.id ? { background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' } : {}}>
                  <div className="flex items-center gap-2.5">
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </div>
                  {item.badge && (
                    <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${
                      activeTab === item.id ? 'bg-white/30 text-white' : 'bg-[#2cbdc4] text-white'
                    }`}>{item.badge}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="px-3 pb-5 border-t border-gray-100 pt-3">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle"
              className="lg:hidden w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 flex-shrink-0">
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <span className="text-xs text-gray-400 hidden sm:inline">Dashboard / </span>
              <span className="text-sm font-bold text-gray-900 truncate">{TAB_LABELS[activeTab] || activeTab}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <PointsBadge token={token} onClick={() => setActiveTab('rewards')} />
            <button className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors">
              <Bell className="w-4 h-4" />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(m => !m)}
                className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black flex-shrink-0 hover:opacity-80 transition-opacity"
                style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                title={user?.full_name}>
                {userInitials}
              </button>
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute right-0 top-11 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100 mb-1">
                      <p className="text-sm font-bold text-gray-900 truncate">{user?.full_name}</p>
                      <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                    </div>
                    <button onClick={() => { setActiveTab('profile'); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <User className="w-4 h-4" /> My Profile
                    </button>
                    <button onClick={() => { setActiveTab('rewards'); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                      <Trophy className="w-4 h-4" /> Rewards
                    </button>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <VerificationBanner token={token} userRole="landlord" onVerifyClick={() => setShowVerifyFlow(true)} />
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          <div className="max-w-5xl mx-auto">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>

    {showVerifyFlow && (
      <VerificationFlow token={token} onClose={() => setShowVerifyFlow(false)} onComplete={() => setShowVerifyFlow(false)} />
    )}

    {/* Contact Student Modal */}
    {contactReq && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(4,30,43,0.7)', backdropFilter: 'blur(8px)' }}>
        <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
          <div className="h-1.5 w-full" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }} />
          <div className="p-7">
            <h3 className="text-xl font-black text-gray-900 mb-1">Contact {contactReq.student_name || 'Student'}</h3>
            <p className="text-sm text-gray-400 mb-5">Introduce yourself and your property to start a conversation.</p>
            {msgSent ? (
              <div className="py-8 text-center">
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-7 h-7 text-green-500" />
                </div>
                <p className="font-bold text-gray-900">Message sent!</p>
                <p className="text-sm text-gray-400 mt-1">Redirecting to messages…</p>
              </div>
            ) : (
              <>
                <div className="bg-gray-50 rounded-2xl p-4 mb-4 text-sm text-gray-700">
                  <p className="font-semibold mb-0.5">{contactReq.title}</p>
                  <p className="text-gray-400">{contactReq.location} · A${contactReq.budget}/wk</p>
                </div>
                <textarea
                  value={contactMsg}
                  onChange={e => setContactMsg(e.target.value)}
                  placeholder={`Hi ${(contactReq.student_name || 'there').split(' ')[0]}, I have a property in ${contactReq.location} that might be perfect for you…`}
                  rows={4}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/20 resize-none mb-4"
                />
                <div className="flex gap-2">
                  <Button className="flex-1 text-white" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                    disabled={!contactMsg.trim() || sendingMsg}
                    onClick={sendContactMessage}>
                    {sendingMsg ? 'Sending…' : 'Send Message'}
                  </Button>
                  <Button variant="outline" onClick={() => setContactReq(null)}>Cancel</Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )}

    {/* View Profile Modal */}
    {viewReq && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(4,30,43,0.7)', backdropFilter: 'blur(8px)' }}>
        <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
          <div className="h-1.5 w-full" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }} />
          <div className="p-7">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-xl font-black text-gray-900">{viewReq.title}</h3>
                <p className="text-sm text-gray-400 mt-0.5">{viewReq.student_name} · Looking for {viewReq.roomType || 'accommodation'}</p>
              </div>
              <span className="text-[#094f6f] font-black text-lg flex-shrink-0 ml-3">A${viewReq.budget}/wk</span>
            </div>
            <div className="space-y-3 mb-5">
              {[
                { label: 'Location', value: viewReq.location },
                { label: 'Move-in Date', value: viewReq.moveInDate },
                { label: 'Lease Duration', value: viewReq.duration },
                { label: 'Room Type', value: viewReq.roomType },
              ].filter(i => i.value).map(item => (
                <div key={item.label} className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">{item.label}</span>
                  <span className="text-sm font-semibold text-gray-800">{item.value}</span>
                </div>
              ))}
            </div>
            {viewReq.description && (
              <div className="bg-gray-50 rounded-2xl p-4 mb-5">
                <p className="text-xs text-gray-400 font-medium mb-1">Notes from student</p>
                <p className="text-sm text-gray-700 leading-relaxed">{viewReq.description}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button className="flex-1 text-white" style={{ background: 'linear-gradient(135deg,#2cbdc4,#094f6f)' }}
                onClick={() => { setViewReq(null); setContactReq(viewReq); setContactMsg(''); setMsgSent(false); }}>
                Contact Student
              </Button>
              <Button variant="outline" onClick={() => setViewReq(null)}>Close</Button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default LandlordDashboard;
